import * as XLSX from 'xlsx';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import i18next from '@/lib/i18n';
import {
  buildHeaderToFieldMap,
  extractErrors,
  normalizeRow,
  readDataSheetRows,
  type FieldError
} from '@/lib/excel';
import type { InitializeProcessInput, ProcessInput } from '@bliks-io/contracts';
import type {
  BulkProcessRowInput,
  InitializeProcessDraft,
  ProcessDraftBase,
  ProcessParseResult,
  ProcessRowData
} from './types';

/**
 * Create a process schema with translated error messages
 * Factory function ensures messages are translated at validation time
 */
function createProcessSchema() {
  return z.object({
    name: z.string().min(1, i18next.t('bulkOnboarding.validation.nameRequired')),
    description: z.string().min(1, i18next.t('bulkOnboarding.validation.descriptionRequired')),
    department: z.string().min(1, i18next.t('bulkOnboarding.validation.departmentRequired'))
  });
}

/**
 * Wizard variant: the Prozessverantwortliche:r is identified by a required
 * email column (initialize endpoint resolves it to a user); the accountable
 * person by an optional one.
 */
export function createInitializeProcessSchema() {
  return createProcessSchema().extend({
    // An empty cell also fails the email format, so one message covers both.
    ownerEmail: z.email(i18next.t('bulkOnboarding.validation.ownerEmailInvalid')),
    accountableEmail: z
      .email(i18next.t('bulkOnboarding.validation.accountableEmailInvalid'))
      .or(z.literal(''))
      .optional()
  });
}

/**
 * Shared parsing core: reads the data sheet, maps translated headers to
 * fields, validates each row, and keeps invalid rows (with errors) so the
 * table can display them.
 */
function parseProcessRows<TData extends ProcessRowData>(
  file: File,
  buildRowData: (normalized: Record<string, string | undefined>) => TData,
  schema: z.ZodType<TData>
): Promise<ProcessParseResult<ProcessDraftBase<TData>>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const headerToFieldMap = buildHeaderToFieldMap('processes');
        const jsonData = readDataSheetRows(workbook, headerToFieldMap);

        const drafts: ProcessDraftBase<TData>[] = [];
        let validRows = 0;
        let invalidRows = 0;

        jsonData.forEach(({ cells, rowNumber }) => {
          const normalized = normalizeRow(cells, headerToFieldMap);
          const rowData = buildRowData(normalized);

          // Skip fully empty rows
          if (Object.values(rowData).every((value) => !value)) {
            return;
          }

          const result = schema.safeParse(rowData);
          if (result.success) {
            drafts.push({ id: uuidv4(), data: result.data, isValid: true, rowNumber });
            validRows++;
          } else {
            invalidRows++;
            drafts.push({
              id: uuidv4(),
              data: rowData,
              isValid: false,
              errors: extractErrors(result.error),
              rowNumber
            });
          }
        });

        resolve({ drafts, totalRows: drafts.length, validRows, invalidRows });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsBinaryString(file);
  });
}

/**
 * Post-onboarding bulk variant: an optional accountable email column. The
 * page resolves it to a user id at the import boundary; an empty cell keeps
 * the dynamic department-manager fallback.
 */
function createBulkProcessSchema() {
  return createProcessSchema().extend({
    accountableEmail: z
      .email(i18next.t('bulkOnboarding.validation.accountableEmailInvalid'))
      .or(z.literal(''))
      .optional()
  });
}

/**
 * Parse Excel file for the post-onboarding bulk creation (owner falls back
 * to the department manager server-side).
 */
export async function parseProcessesFromExcel(
  file: File
): Promise<ProcessParseResult<ProcessDraftBase<BulkProcessRowInput>>> {
  return parseProcessRows<BulkProcessRowInput>(
    file,
    (normalized) => ({
      name: normalized.name || '',
      description: normalized.description || '',
      department: normalized.department || '',
      accountableEmail: normalized.accountableEmail || ''
    }),
    createBulkProcessSchema()
  );
}

/**
 * Parse Excel file for the initial-setup wizard (ownerEmail required).
 */
export async function parseInitializeProcessesFromExcel(
  file: File
): Promise<ProcessParseResult<InitializeProcessDraft>> {
  return parseProcessRows<InitializeProcessInput>(
    file,
    (normalized) => ({
      name: normalized.name || '',
      description: normalized.description || '',
      department: normalized.department || '',
      ownerEmail: normalized.ownerEmail || '',
      accountableEmail: normalized.accountableEmail || ''
    }),
    createInitializeProcessSchema()
  );
}

/**
 * Validate process data manually (for form submissions)
 * Returns validation result with errors if invalid
 */
export function validateProcessData(data: ProcessInput): {
  isValid: boolean;
  errors?: FieldError[];
} {
  const result = createProcessSchema().safeParse(data);
  return result.success
    ? { isValid: true }
    : { isValid: false, errors: extractErrors(result.error) };
}
